import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';
import {
  TUpdateColumnValidation,
  UpdateColumnValidationSchema,
  useUpdateColumnMutation,
} from '../../api/update-column.api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_COLUMN_QUERY_KEY } from '../../api/get-list-column.api';
import { handleDomBubbleEvent } from '@/utils/dom';
import { COLUMN_COLOR } from '../../constants/column.constant';
import { cn } from '@/utils/cn';
import { deepEqual } from '@/utils/array-utils';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { Input } from '@/components/_ui/input';

export type TUpdateColumnModalType = 'UpdateColumnModal';
export interface TUpdateColumnModalProps {
  spaceId: string;
  spaceColumnId: string;
  name?: string | undefined;
  columnIndex?: string | undefined;
  color?: string | undefined;
}
const UpdateColumnModal = memo(({ spaceId, spaceColumnId, name, columnIndex, color }: TUpdateColumnModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TUpdateColumnValidation>({
    resolver: zodResolver(UpdateColumnValidationSchema),
    mode: 'onChange',
  });

  const mutationUpdateColumn = useUpdateColumnMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || 'Error updating ticketing space column, please try again!';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([
          // queryClient.invalidateQueries({
          //   queryKey: [GET_SINGLE_SPACE_QUERY_KEY, { spaceId }],
          // }),
          // queryClient.invalidateQueries({
          //   queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
          // }),
          queryClient.invalidateQueries({
            queryKey: [
              GET_LIST_COLUMN_QUERY_KEY,
              {
                spaceId: spaceId,
                search: undefined,
                page: undefined,
                limit: undefined,
                sortDir: undefined,
                sortBy: undefined,
              },
            ],
          }),
        ]);
        modalActions.closeModal('UpdateColumnModal');
        form.reset();
        showToast({
          message: 'Ticketing space column edited successfully',
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenUpdateColumnModal = useMemo(() => {
    return modalActions.checkIsOpenModal('UpdateColumnModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationUpdateColumn.isPending;
  }, [mutationUpdateColumn.isPending]);

  const isLoadingForm = useMemo(() => {
    return mutationUpdateColumn.isPending;
  }, [mutationUpdateColumn.isPending]);

  // EFFECTS
  // --- Sync with props ---
  useEffect(() => {
    if (spaceId && spaceColumnId) {
      form.reset({
        spaceColumnId,
        columnIndex: columnIndex || '',
        name: name || '',
        color: color || '',
      });
    }
  }, [spaceId, spaceColumnId, columnIndex, name, color, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('UpdateColumnModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  const handleSubmitForm = useCallback(
    (data: TUpdateColumnValidation) => {
      // Original values (from props)
      const originalValues = {
        name: name || '',
        columnIndex: columnIndex || '',
        color: color || '',
      };

      // Initialize payload with required IDs
      const dataToUpdate: {
        spaceColumnId: string;
      } & Partial<TUpdateColumnValidation> = {
        spaceColumnId,
      };

      // Compare and add only changed scalar fields
      (['name', 'columnIndex', 'color'] as const).forEach((key) => {
        if (!deepEqual(data[key], originalValues[key])) {
          (dataToUpdate as Record<string, unknown>)[key] = data[key];
        }
      });

      // Mutate only changed fields
      mutationUpdateColumn.mutate(dataToUpdate);
    },
    [mutationUpdateColumn, spaceColumnId, name, columnIndex, color]
  );

  return (
    <Modal
      isOpen={isOpenUpdateColumnModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Update Column</ModalTitle>
        <ModalDescription>Update the column to define a new stage in your workflow.</ModalDescription>
      </ModalHeader>
      <ModalContent>
        <form
          id="update-column-form"
          className="flex flex-col gap-[16px]"
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
        >
          <div className="flex flex-col gap-[8px]">
            <label htmlFor="name" className="text-[12px] text-[#4B4B4B] font-[700]">
              Name
            </label>
            <Input {...form.register('name')} id="name" placeholder="Enter column name" className="bg-white" />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-[8px] px-[2px]">
            <label htmlFor="color" className="text-[12px] text-[#4B4B4B] font-[700]">
              Color
            </label>
            <div className="flex gap-[8px]">
              {Object.entries(COLUMN_COLOR).map(([_key, color], index) => {
                const isSelected = form.watch('color') === (color as string);

                return (
                  <button
                    key={index + 1}
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color as string);
                    }}
                    className={cn('w-[16px] h-[16px] rounded-full cursor-pointer border transition-shadow', {
                      'border-[#0C8DBF] ring-2 ring-[#0C8DBF]/40': isSelected,
                      'border-transparent': !isSelected,
                    })}
                    style={{ backgroundColor: color as string }}
                  ></button>
                );
              })}
            </div>
            {form.formState.errors.color && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.color.message}</p>
            )}
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="update-column-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Save Changes
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
UpdateColumnModal.displayName = 'UpdateColumnModal';
export default UpdateColumnModal;
